import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import i18n from '@/i18n'
import { motion, AnimatePresence } from 'framer-motion'
import { Calendar, MapPin, Clock, Users, Video, CheckCircle, LogOut, ChevronDown, ChevronUp, Download } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAuthStore } from '@/stores/authStore'
import api from '@/api/client'
import { toast } from '@/components/ui/toaster'
import { SkeletonCard } from '@/components/Skeleton'

interface Participant {
  userId: string
  username: string
}

interface Event {
  _id: string
  title: string
  description: string
  date: string
  location: string
  isOnline?: boolean
  meetingUrl?: string
  participants?: Participant[]
}

export default function Events() {
  const { isAuthenticated, user } = useAuthStore()
  const queryClient = useQueryClient()
  const [expanded, setExpanded] = useState<string | null>(null)
  const { t } = useTranslation()

  const { data: events, isLoading } = useQuery({
    queryKey: ['events'],
    queryFn: () => api.get('/events').then(r => r.data)
  })

  const registerMutation = useMutation({
    mutationFn: (eventId: string) => api.post('/events/register', { eventId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] })
      toast({ title: t('events.registered') })
    },
    onError: (err: any) => toast({ title: err.response?.data?.error || t('events.error'), variant: 'destructive' })
  })

  const leaveMutation = useMutation({
    mutationFn: (eventId: string) => api.post(`/events/${eventId}/leave`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] })
      toast({ title: t('events.leftEvent') })
    },
    onError: (err: any) => toast({ title: err.response?.data?.error || t('events.error'), variant: 'destructive' })
  })

  const isRegistered = (event: Event) => event.participants?.some(p => String(p.userId) === String(user?.id))

  const handleDownloadIcs = async (event: Event) => {
    try {
      const res = await api.get(`/events/${event._id}/ics`, { responseType: 'blob' })
      const blob = new Blob([res.data], { type: 'text/calendar' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `nyxeverix-event-${event._id}.ics`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err: any) {
      toast({ title: err.response?.data?.error || t('events.error'), variant: 'destructive' })
    }
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <div className="text-center mb-8">
        <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
          <Calendar className="w-7 h-7 text-primary" />
        </div>
        <h1 className="font-display text-2xl font-bold">{t('events.title')}</h1>
        <p className="text-muted-foreground mt-2">{t('events.subtitle')}</p>
      </div>

      <div className="grid gap-4">
        {isLoading ? (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : (
          events?.map((event: Event) => {
          const registered = isRegistered(event)
          const isOpen = expanded === event._id
          return (
            <motion.div
              key={event._id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-5 rounded-2xl bg-card border border-border hover:border-primary/20 transition-colors"
            >
              <div className="flex flex-col md:flex-row gap-4">
                <div className="md:w-20 shrink-0 flex md:flex-col items-center justify-center gap-2 md:gap-0 p-3 rounded-xl bg-primary/10 text-primary">
                  <span className="text-xl md:text-2xl font-bold">{new Date(event.date).getDate()}</span>
                  <span className="text-xs uppercase">{new Date(event.date).toLocaleString(i18n.language === 'tr' ? 'tr-TR' : 'en-US', { month: 'short' })}</span>
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <Link to={`/events/${event._id}`} className="font-semibold text-lg hover:text-primary transition-colors">
                        {event.title}
                      </Link>
                      <p className="text-sm text-muted-foreground mt-1">{event.description}</p>
                    </div>
                    {event.isOnline && (
                      <span className="shrink-0 flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-blue-500/10 text-blue-500">
                        <Video className="w-3 h-3" /> {t('common.online')}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-3 mt-3 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {new Date(event.date).toLocaleTimeString(i18n.language === 'tr' ? 'tr-TR' : 'en-US', { hour: '2-digit', minute: '2-digit' })}</span>
                    <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {event.location}</span>
                    <button
                      onClick={() => setExpanded(isOpen ? null : event._id)}
                      className="flex items-center gap-1 hover:text-foreground transition-colors"
                    >
                      <Users className="w-4 h-4" /> {event.participants?.length || 0} {t('events.participants')}
                      {isOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    </button>
                  </div>
                </div>
                <div className="shrink-0 flex flex-col gap-2 items-end">
                  {isAuthenticated ? (
                    registered ? (
                      <>
                        <Button size="sm" variant="outline" className="rounded-lg text-green-500 border-green-500/30" disabled>
                          <CheckCircle className="w-4 h-4 mr-1" /> {t('events.registeredButton')}
                        </Button>
                        {event.isOnline && (
                          (() => {
                            const isExternal = event.meetingUrl && !event.meetingUrl.includes('8x8.vc') && !event.meetingUrl.includes('meet.jit.si')
                            if (isExternal) {
                              return (
                                <a href={event.meetingUrl} target="_blank" rel="noopener noreferrer" className="text-xs px-3 py-1.5 rounded-lg bg-blue-500/10 text-blue-500 hover:bg-blue-500/20 transition-colors">
                                  <Video className="w-3 h-3 inline mr-1" /> {t('events.joinMeeting')}
                                </a>
                              )
                            }
                            return (
                              <Link to={`/events/${event._id}`} className="text-xs px-3 py-1.5 rounded-lg bg-blue-500/10 text-blue-500 hover:bg-blue-500/20 transition-colors">
                                <Video className="w-3 h-3 inline mr-1" /> {t('events.joinMeeting')}
                              </Link>
                            )
                          })()
                        )}
                        <button onClick={() => leaveMutation.mutate(event._id)} className="text-xs text-muted-foreground hover:text-destructive transition-colors">
                          <LogOut className="w-3 h-3 inline mr-1" /> {t('events.leave')}
                        </button>
                      </>
                    ) : (
                      <Button size="sm" className="rounded-lg" onClick={() => registerMutation.mutate(event._id)} disabled={registerMutation.isPending}>
                        <Calendar className="w-4 h-4 mr-1" /> {t('events.join')}
                      </Button>
                    )
                  ) : (
                    <Button size="sm" variant="outline" className="rounded-lg" disabled>{t('events.joinLogin')}</Button>
                  )}
                  <button
                    type="button"
                    onClick={() => handleDownloadIcs(event)}
                    className="text-xs flex items-center gap-1 text-muted-foreground hover:text-primary transition-colors mt-1"
                    aria-label={t('events.addToCalendar')}
                  >
                    <Download className="w-3 h-3" /> {t('events.addToCalendar')}
                  </button>
                </div>
              </div>

              <AnimatePresence>
                {isOpen && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mt-4 pt-4 border-t border-border overflow-hidden">
                    <p className="text-xs font-medium text-muted-foreground mb-2">{t('events.participantsLabel')}</p>
                    <div className="flex flex-wrap gap-2">
                      {event.participants?.length ? event.participants.map(p => (
                        <span key={p.userId} className="text-xs px-2 py-1 rounded-lg bg-secondary">{p.username}</span>
                      )) : <span className="text-xs text-muted-foreground">{t('events.noParticipants')}</span>}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )
        }))}
        {!isLoading && (!events || events.length === 0) && (
          <div className="text-center py-12 text-muted-foreground">{t('events.noEvents')}</div>
        )}
      </div>
    </div>
  )
}
